import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Linkedin,
  ShieldAlert,
  CheckCircle2,
  Circle,
  ExternalLink,
  AlertTriangle,
  Loader2,
  Copy,
  Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import GradientMesh from "@/components/GradientMesh";
import GlassCard from "@/components/GlassCard";

const SECTION_ORDER = [
  "app_identity",
  "products_required",
  "scopes_to_request",
  "redirect_uris",
  "manual_followups",
];

function copyToClipboard(text) {
  try {
    navigator.clipboard.writeText(text || "");
    toast.success("Copied to clipboard");
  } catch (_e) {
    toast.error("Couldn't copy — please select and copy manually.");
  }
}

function FlagBadge({ on, label }) {
  return (
    <span
      className={
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium " +
        (on
          ? "bg-[color:rgba(46,204,113,0.12)] text-[color:var(--success)]"
          : "bg-[color:rgba(255,138,61,0.12)] text-[color:var(--accent-warm)]")
      }
      data-testid={`linkedin-flag-${on ? "on" : "off"}`}
    >
      {on ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
      {label || (on ? "ON" : "OFF")}
    </span>
  );
}

function RuntimeRow({ label, value, hint, on }) {
  const isUrl = typeof value === "string" && /^https?:\/\//.test(value);
  return (
    <div className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-3 py-2.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-[color:var(--text-1)]">{label}</div>
          {hint ? (
            <div className="text-[11px] text-[color:var(--text-3)] mt-0.5 leading-relaxed">{hint}</div>
          ) : null}
        </div>
        <div className="shrink-0">
          {on === undefined ? (
            !isUrl ? (
              <div className="text-[11px] text-[color:var(--text-2)] font-mono">{value || "—"}</div>
            ) : null
          ) : (
            <FlagBadge on={on} />
          )}
        </div>
      </div>
      {on === undefined && isUrl ? (
        <div className="mt-2 text-[11px] text-[color:var(--text-2)] font-mono break-all leading-relaxed">
          {value}
        </div>
      ) : null}
    </div>
  );
}

function ChecklistItem({ item }) {
  const value = item.value || "";
  const isCopyable = typeof value === "string" && value.length > 0 && value.length < 240 && !value.includes("\n");
  return (
    <div
      className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3"
      data-testid={`linkedin-checklist-item-${item.key}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-[color:var(--text-1)]">{item.label}</div>
          {value ? (
            <div className="text-[12px] text-[color:var(--text-2)] mt-1 break-all leading-relaxed font-mono whitespace-pre-wrap">
              {value}
            </div>
          ) : (
            <div className="text-[11px] text-[color:var(--text-3)] italic mt-1">
              Set this value in the LinkedIn Developer Portal.
            </div>
          )}
          {item.note ? (
            <div className="text-[11px] text-[color:var(--text-3)] mt-2 leading-relaxed">{item.note}</div>
          ) : null}
        </div>
        {isCopyable ? (
          <Button
            size="icon"
            variant="ghost"
            className="shrink-0 h-7 w-7 text-[color:var(--text-3)] hover:text-[color:var(--text-1)]"
            onClick={() => copyToClipboard(value)}
            data-testid={`linkedin-checklist-copy-${item.key}`}
            aria-label={`Copy ${item.label}`}
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function ChecklistSection({ sectionKey, section }) {
  return (
    <GlassCard className="p-5" data-testid={`linkedin-section-${sectionKey}`}>
      <div className="font-display text-lg font-semibold text-[color:var(--text-1)] mb-3">
        {section.title}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {section.items.map((item) => (
          <ChecklistItem key={item.key} item={item} />
        ))}
      </div>
    </GlassCard>
  );
}

function TestPublishCard({ adminConnection, onChanged }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const res = await api.post("/admin/connections/linkedin/test-publish", { text: text.trim() });
      setResult(res.data);
      if (res.data.ok) {
        toast.success("LinkedIn post published successfully");
        onChanged?.();
      } else {
        toast.error(res.data.error || `LinkedIn API returned ${res.data.status_code}`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Test publish failed";
      setResult({ ok: false, error: detail, status_code: e?.response?.status });
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const disabled = !adminConnection?.connected;

  return (
    <GlassCard className="p-5" data-testid="linkedin-test-publish">
      <div className="font-display text-lg font-semibold text-[color:var(--text-1)] mb-2">
        Test publish to LinkedIn
      </div>
      <div className="text-xs text-[color:var(--text-3)] mb-3 leading-relaxed">
        Sends a real text-only post to YOUR LinkedIn personal profile using the admin's connection.
        Useful for verifying the integration end-to-end. The post is real — please delete it from
        LinkedIn afterwards if you don't want it on your timeline.
      </div>

      {disabled ? (
        <div className="rounded-md border border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.06)] p-3 text-xs text-[color:var(--accent-warm)] flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            Connect LinkedIn from Settings → Connections first (as the admin user), then come back here to run a test publish.
          </div>
        </div>
      ) : (
        <>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Leave blank for an auto-generated timestamped test post."
            rows={3}
            maxLength={2900}
            disabled={busy}
            className="w-full rounded-md bg-[color:var(--bg-0)] border border-[color:var(--border-subtle)] text-[color:var(--text-1)] text-xs px-2 py-1.5 font-mono resize-none"
            data-testid="linkedin-test-publish-input"
          />
          <div className="flex items-center justify-between gap-2 mt-2">
            <div className="text-[10px] text-[color:var(--text-3)]">{text.length}/2900</div>
            <Button
              size="sm"
              onClick={run}
              disabled={busy}
              data-testid="linkedin-test-publish-button"
            >
              {busy ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Send className="h-3.5 w-3.5 mr-1.5" />
              )}
              {busy ? "Publishing…" : "Send test post"}
            </Button>
          </div>
          {result ? (
            <div
              className={
                "mt-3 rounded-md border p-2.5 text-[11px] font-mono leading-relaxed break-all " +
                (result.ok
                  ? "border-[color:rgba(46,204,113,0.35)] bg-[color:rgba(46,204,113,0.06)] text-[color:var(--success)]"
                  : "border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.06)] text-[color:var(--accent-warm)]")
              }
              data-testid="linkedin-test-publish-result"
            >
              <div className="font-display font-semibold text-[color:var(--text-1)] not-italic mb-1">
                {result.ok ? "Success" : `Failed (status ${result.status_code || "n/a"})`}
              </div>
              {result.post_urn ? <div>URN: {result.post_urn}</div> : null}
              {result.external_url ? (
                <div>
                  URL:{" "}
                  <a
                    href={result.external_url}
                    target="_blank"
                    rel="noreferrer"
                    className="underline"
                  >
                    {result.external_url}
                  </a>
                </div>
              ) : null}
              {result.error ? <div className="mt-1">{result.error}</div> : null}
            </div>
          ) : null}
        </>
      )}
    </GlassCard>
  );
}

export default function AdminLinkedInChecklistPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/linkedin/checklist");
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="relative">
      <GradientMesh />
      <div className="relative">
        <PageHeader
          title="LinkedIn integration setup"
          subtitle="LinkedIn personal-profile posting via OAuth 2.0 + Sign In with LinkedIn (OIDC). Owner-only — nothing here is exposed to customers."
          testidPrefix="admin-linkedin-checklist"
        />

        <div className="rounded-md border border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.04)] p-3 mb-5 text-xs text-[color:var(--accent-warm)] flex items-start gap-2">
          <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="leading-relaxed">
            <strong className="text-[color:var(--text-1)]">Rotate the client secret.</strong>{" "}
            The LINKEDIN_CLIENT_SECRET was pasted in chat history during setup. Click
            "Regenerate client secret" in linkedin.com/developers → your app → Auth tab, then update
            LINKEDIN_CLIENT_SECRET in /app/backend/.env and restart the backend.
          </div>
        </div>

        {loading ? (
          <GlassCard className="p-10 flex items-center justify-center text-[color:var(--text-3)]">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading checklist…
          </GlassCard>
        ) : err ? (
          <GlassCard className="p-6 text-sm text-[color:var(--accent-warm)] flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold text-[color:var(--text-1)]">Couldn't load checklist</div>
              <div className="mt-1">{err}</div>
            </div>
          </GlassCard>
        ) : !data ? null : (
          <div className="space-y-5">
            {/* Runtime status + admin connection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <GlassCard className="p-5" data-testid="linkedin-runtime-state">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-8 w-8 rounded-md flex items-center justify-center bg-[color:rgba(10,102,194,0.18)] text-[color:#0A66C2]">
                    <Linkedin className="h-4 w-4" />
                  </div>
                  <div className="font-display text-base font-semibold text-[color:var(--text-1)]">
                    Server state
                  </div>
                </div>
                <div className="space-y-3">
                  <RuntimeRow
                    label="LinkedIn credentials configured"
                    hint="LINKEDIN_CLIENT_ID + LINKEDIN_CLIENT_SECRET + LINKEDIN_REDIRECT_URI"
                    on={data.runtime_flags?.configured}
                  />
                  <RuntimeRow
                    label="LINKEDIN_LIVE_ENABLED"
                    hint="Master kill-switch. When OFF, customers see LinkedIn as 'Planned'."
                    on={data.runtime_flags?.live_enabled}
                  />
                  <RuntimeRow
                    label="Scopes requested"
                    value={data.runtime_flags?.scopes_requested}
                    hint="These are the OAuth scopes Postmoro asks for during sign-in."
                  />
                  <RuntimeRow
                    label="Redirect URI (live)"
                    value={data.runtime_flags?.redirect_uri}
                    hint="Must exactly match the redirect URI saved in your LinkedIn app's Auth tab."
                  />
                </div>
              </GlassCard>

              <GlassCard className="p-5" data-testid="linkedin-admin-connection">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-8 w-8 rounded-md flex items-center justify-center bg-[color:rgba(10,102,194,0.18)] text-[color:#0A66C2]">
                    <Linkedin className="h-4 w-4" />
                  </div>
                  <div className="font-display text-base font-semibold text-[color:var(--text-1)]">
                    Admin's LinkedIn connection
                  </div>
                  <FlagBadge
                    on={!!data.admin_connection?.connected}
                    label={data.admin_connection?.connected ? "CONNECTED" : "NOT CONNECTED"}
                  />
                </div>
                {data.admin_connection?.connected ? (
                  <div className="space-y-1.5 text-xs text-[color:var(--text-2)]">
                    <div>
                      <span className="text-[color:var(--text-3)]">Name:</span>{" "}
                      <span className="text-[color:var(--text-1)]">{data.admin_connection.name || "—"}</span>
                    </div>
                    <div>
                      <span className="text-[color:var(--text-3)]">Email:</span>{" "}
                      <span className="text-[color:var(--text-1)]">{data.admin_connection.email || "—"}</span>
                    </div>
                    <div className="font-mono break-all">
                      <span className="text-[color:var(--text-3)]">Member URN:</span>{" "}
                      {data.admin_connection.member_urn || "—"}
                    </div>
                    <div>
                      <span className="text-[color:var(--text-3)]">Granted scopes:</span>{" "}
                      <span className="font-mono">
                        {(data.admin_connection.granted_scopes || []).join(" ") || "—"}
                      </span>
                    </div>
                    <div>
                      <span className="text-[color:var(--text-3)]">Token expires:</span>{" "}
                      {data.admin_connection.token_expires_at || "—"}
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-[color:var(--text-3)] leading-relaxed">
                    Connect LinkedIn from <strong>Settings → Connections → LinkedIn</strong> (as
                    the admin user), then return here to run a test publish.
                  </div>
                )}
                <div className="mt-3">
                  <a
                    href="https://www.linkedin.com/developers/apps"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-[color:var(--brand-primary)] hover:underline"
                  >
                    <ExternalLink className="h-3 w-3" /> Open LinkedIn Developer Portal
                  </a>
                </div>
              </GlassCard>
            </div>

            {/* Test publish row */}
            <TestPublishCard adminConnection={data.admin_connection} onChanged={load} />

            {/* Structured checklist sections */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-5"
            >
              {SECTION_ORDER.map((key) => {
                const section = data.checklist?.[key];
                if (!section) return null;
                return <ChecklistSection key={key} sectionKey={key} section={section} />;
              })}
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}
